import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ChartBarIcon, TargetIcon, GlobeIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon, ShieldCheckIcon, BeakerIcon, FileTextIcon, ClipboardCheckIcon, AwardIcon, TrendingUpIcon, UserIcon, BuildingIcon, BarChart4Icon, ArrowRightIcon, SearchIcon, FilterIcon, StarIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
export const ProductReviewsPage = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedCertificationLevel, setCertificationLevel] = useState('All');
  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 50
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  // Sample product reviews
  const productReviews = [{
    id: 1,
    name: 'BioBalance Pro Prebiotic',
    category: 'Prebiotic',
    company: 'NutriTech Labs',
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    healthClaim: 'Supports gut microbiome diversity and digestive health',
    certificationLevel: 3,
    transparencyScore: 92,
    purityResults: 'Passed all safety and purity tests. No contaminants detected.',
    bioavailabilityResults: 'High survivability through digestive simulation. Reaches target areas effectively.',
    efficacySummary: 'Claims fully supported by evidence',
    recommendation: 'Recommended',
    keyFindings: ['Contains all strains listed on label at specified quantities', 'Demonstrates measurable impact on beneficial gut bacteria in lab models', 'Clinical trial showed 42% improvement in microbiome diversity after 8 weeks', 'Statistically significant reduction in digestive discomfort reported by study participants']
  }, {
    id: 2,
    name: 'ImmunoStrength Plus',
    category: 'Immune Support',
    company: 'VitalCore Supplements',
    image: 'https://images.unsplash.com/photo-1587854692152-cbe660dbde88?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    healthClaim: 'Boosts immune function and reduces inflammation',
    certificationLevel: 2,
    transparencyScore: 78,
    purityResults: 'Passed safety tests. Minor variance in vitamin C content (12% below label claim).',
    bioavailabilityResults: 'Moderate absorption rate in digestive simulation.',
    efficacySummary: 'Claims partially supported by evidence',
    recommendation: 'Conditionally Recommended',
    keyFindings: ['Contains key ingredients but with some variance from label claims', 'Shows moderate anti-inflammatory activity in cell models', 'Increases NK cell activity in lab testing', 'No human clinical trials completed yet to verify immune enhancement claims']
  }, {
    id: 3,
    name: 'GutGuard Digestive Enzymes',
    category: 'Digestive Support',
    company: 'BioCare Health',
    image: 'https://images.unsplash.com/photo-1550572017-edd951b55104?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    healthClaim: 'Improves nutrient absorption and reduces digestive discomfort',
    certificationLevel: 1,
    transparencyScore: 85,
    purityResults: 'Passed all safety tests. Enzyme activity matches label claims.',
    bioavailabilityResults: 'Not tested at Level 1 certification.',
    efficacySummary: 'Safety and content verified, efficacy not validated',
    recommendation: 'Verified for Safety',
    keyFindings: ['Contains all enzymes listed at specified activity levels', 'Free from harmful contaminants and allergens', 'Manufacturing follows cGMP standards', 'No efficacy testing completed to verify digestive improvement claims']
  }, {
    id: 4,
    name: 'MicrobiomePlus Daily',
    category: 'Probiotic',
    company: 'Vitality Sciences',
    image: 'https://images.unsplash.com/photo-1576671414121-aa0c81c869e3?ixlib=rb-1.2.1&auto=format&fit=crop&w=800&q=80',
    healthClaim: 'Restores microbiome balance and improves gut health',
    certificationLevel: 2,
    transparencyScore: 88,
    purityResults: 'Passed safety tests. CFU count 15% lower than label claim.',
    bioavailabilityResults: 'Good survivability in acid conditions. Colonization demonstrated in lab models.',
    efficacySummary: 'Claims partially supported by evidence',
    recommendation: 'Recommended with Notes',
    keyFindings: ['Contains all probiotic strains listed but at lower concentrations than claimed', 'Demonstrates ability to temporarily colonize the gut in lab models', 'Shows positive modulation of gut microbiome composition', 'Further human studies needed to validate long-term health benefits']
  }];
  const categories = ['All', 'Probiotic', 'Prebiotic', 'Immune Support', 'Digestive Support'];
  const certificationLevels = ['All', 1, 2, 3];
  const filteredProducts = productReviews.filter(product => {
    const categoryMatch = selectedCategory === 'All' || product.category === selectedCategory;
    const levelMatch = selectedCertificationLevel === 'All' || product.certificationLevel === selectedCertificationLevel;
    return categoryMatch && levelMatch;
  });
  const getCertificationBadge = level => {
    switch (level) {
      case 1:
        return <div className="flex items-center" data-id="element-1365">
            <ShieldCheckIcon size={16} className="text-blue-600 mr-1" data-id="element-1366" />
            <span className="text-sm font-medium" data-id="element-1367">
              Level 1: Evidence Screen
            </span>
          </div>;
      case 2:
        return <div className="flex items-center" data-id="element-1368">
            <BeakerIcon size={16} className="text-blue-600 mr-1" data-id="element-1369" />
            <span className="text-sm font-medium" data-id="element-1370">
              Level 2: Mechanistic Validation
            </span>
          </div>;
      case 3:
        return <div className="flex items-center" data-id="element-1371">
            <AwardIcon size={16} className="text-blue-600 mr-1" data-id="element-1372" />
            <span className="text-sm font-medium" data-id="element-1373">
              Level 3: Clinical Validation
            </span>
          </div>;
      default:
        return null;
    }
  };
  const getRecommendationBadge = recommendation => {
    switch (recommendation) {
      case 'Recommended':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1374">
            <CheckCircleIcon size={12} className="mr-1" data-id="element-1375" />
            Recommended
          </span>;
      case 'Conditionally Recommended':
      case 'Recommended with Notes':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800" data-id="element-1376">
            <AlertCircleIcon size={12} className="mr-1" data-id="element-1377" />
            {recommendation}
          </span>;
      case 'Verified for Safety':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-1378">
            <ShieldCheckIcon size={12} className="mr-1" data-id="element-1379" />
            Verified for Safety
          </span>;
      case 'Not Recommended':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" data-id="element-1380">
            <XCircleIcon size={12} className="mr-1" data-id="element-1381" />
            Not Recommended
          </span>;
      default:
        return null;
    }
  };
  return <div className="bg-white w-full" data-id="element-1382">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gray-50" data-id="element-1383">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1384">
          <div className="text-center" data-id="element-1385">
            <h1 className="text-4xl font-bold text-gray-900 sm:text-5xl md:text-6xl" data-id="element-1386">
              Ukweli Product Reviews
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl text-gray-600" data-id="element-1387">
              Evidence-based scientific evaluations that replace subjective
              opinions with verified data
            </p>
          </div>
        </div>
      </section>

      {/* What Product Reviews Are */}
      <section className="py-16 bg-white" data-id="element-1388">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1389">
          <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={sectionVariants} transition={{
          duration: 0.8
        }} className="mb-16" data-id="element-1390">
            <div className="flex items-center justify-center mb-8" data-id="element-1391">
              <div className="bg-blue-100 p-3 rounded-full" data-id="element-1392">
                <div size={28} className="text-blue-600" data-id="element-1393" />
              </div>
              <h2 className="ml-4 text-3xl font-bold text-gray-900" data-id="element-1394">
                What Product Reviews Are
              </h2>
            </div>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-12" data-id="element-1395">
              At the Ukweli Institute, Product Reviews aren't like the casual
              consumer reviews you see on Amazon or blogs — they are
              evidence-based scientific evaluations tied to our certification
              system.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1396">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="bg-gray-50 p-6 rounded-lg shadow-sm" data-id="element-1397">
                <div className="flex items-center mb-4" data-id="element-1398">
                  <FileTextIcon size={20} className="text-blue-600 mr-2" data-id="element-1399" />
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1400">
                    Transparent Reports
                  </h3>
                </div>
                <p className="text-gray-700" data-id="element-1401">
                  Each review is a transparent report on a supplement,
                  probiotic, or nutraceutical that has been tested through the
                  Ukweli Certified™ process.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="bg-gray-50 p-6 rounded-lg shadow-sm" data-id="element-1402">
                <div className="flex items-center mb-4" data-id="element-1403">
                  <ClipboardCheckIcon size={20} className="text-blue-600 mr-2" data-id="element-1404" />
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1405">
                    Evidence Over Anecdotes
                  </h3>
                </div>
                <p className="text-gray-700" data-id="element-1406">
                  Instead of star ratings or anecdotes, the reviews summarize
                  lab results, mechanistic evidence, and clinical outcomes.
                </p>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-gray-50 p-6 rounded-lg shadow-sm" data-id="element-1407">
                <div className="flex items-center mb-4" data-id="element-1408">
                  <UserIcon size={20} className="text-blue-600 mr-2" data-id="element-1409" />
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-1410">
                    Accessible Language
                  </h3>
                </div>
                <p className="text-gray-700" data-id="element-1411">
                  They are written in clear, public-facing language so consumers
                  and clinicians can understand the findings without needing a
                  scientific background.
                </p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* What They Include */}
      <section className="py-16 bg-gray-50" data-id="element-1412">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1413">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-16" data-id="element-1414">
            <div className="flex items-center justify-center mb-8" data-id="element-1415">
              <div className="bg-blue-100 p-3 rounded-full" data-id="element-1416">
                <ChartBarIcon size={28} className="text-blue-600" data-id="element-1417" />
              </div>
              <h2 className="ml-4 text-3xl font-bold text-gray-900" data-id="element-1418">
                What They Include
              </h2>
            </div>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-12" data-id="element-1419">
              A typical Ukweli Product Review provides comprehensive information
              about the product's scientific validation.
            </p>
            <div className="bg-white p-8 rounded-lg shadow-md" data-id="element-1420">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1421">
                <div data-id="element-1422">
                  <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1423">
                    Basic Profile
                  </h3>
                  <ul className="space-y-3 text-gray-700" data-id="element-1424">
                    <li className="flex items-start" data-id="element-1425">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1426" />
                      <span data-id="element-1427">Product name and manufacturer</span>
                    </li>
                    <li className="flex items-start" data-id="element-1428">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1429" />
                      <span data-id="element-1430">
                        Category (e.g., probiotic, vitamin, herbal blend)
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1431">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1432" />
                      <span data-id="element-1433">Intended health claim</span>
                    </li>
                  </ul>
                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4" data-id="element-1434">
                    Certification Level
                  </h3>
                  <ul className="space-y-3 text-gray-700" data-id="element-1435">
                    <li className="flex items-start" data-id="element-1436">
                      <ShieldCheckIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1437" />
                      <span data-id="element-1438">Level 1: Evidence Screen</span>
                    </li>
                    <li className="flex items-start" data-id="element-1439">
                      <BeakerIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1440" />
                      <span data-id="element-1441">Level 2: Mechanistic Validation</span>
                    </li>
                    <li className="flex items-start" data-id="element-1442">
                      <AwardIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1443" />
                      <span data-id="element-1444">Level 3: Clinical Validation</span>
                    </li>
                  </ul>
                </div>
                <div data-id="element-1445">
                  <h3 className="text-xl font-bold text-gray-900 mb-4" data-id="element-1446">
                    Testing Results
                  </h3>
                  <ul className="space-y-3 text-gray-700" data-id="element-1447">
                    <li className="flex items-start" data-id="element-1448">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1449" />
                      <span data-id="element-1450">Purity and safety findings</span>
                    </li>
                    <li className="flex items-start" data-id="element-1451">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1452" />
                      <span data-id="element-1453">Bioavailability and biomarker results</span>
                    </li>
                    <li className="flex items-start" data-id="element-1454">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1455" />
                      <span data-id="element-1456">
                        Evidence from lab models and/or clinical trials
                      </span>
                    </li>
                  </ul>
                  <h3 className="text-xl font-bold text-gray-900 mt-8 mb-4" data-id="element-1457">
                    Conclusions
                  </h3>
                  <ul className="space-y-3 text-gray-700" data-id="element-1458">
                    <li className="flex items-start" data-id="element-1459">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1460" />
                      <span data-id="element-1461">
                        Efficacy Summary: Whether claims are supported,
                        partially supported, or unsupported by the evidence
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1462">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1463" />
                      <span data-id="element-1464">
                        Transparency Score: How openly the company shared data
                        and cooperated with testing
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-1465">
                      <CheckCircleIcon size={20} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1466" />
                      <span data-id="element-1467">
                        Recommendation: Whether the product is "Ukweli
                        Certified" and what that means for consumers
                      </span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Product Review Examples */}
      <section className="py-16 bg-white" data-id="element-1468">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1469">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-12" data-id="element-1470">
            <h2 className="text-3xl font-bold text-gray-900 text-center mb-4" data-id="element-1471">
              Featured Product Reviews
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto text-center mb-8" data-id="element-1472">
              Explore our evidence-based evaluations of wellness products
            </p>
            {/* Filters */}
            <div className="bg-gray-50 p-4 rounded-lg mb-8" data-id="element-1473">
              <div className="flex flex-col md:flex-row justify-between items-center" data-id="element-1474">
                <div className="flex items-center mb-4 md:mb-0" data-id="element-1475">
                  <FilterIcon size={20} className="text-gray-500 mr-2" data-id="element-1476" />
                  <span className="text-gray-700 font-medium" data-id="element-1477">Filter by:</span>
                </div>
                <div className="flex flex-col sm:flex-row gap-4" data-id="element-1478">
                  <div data-id="element-1479">
                    <label htmlFor="category" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1480">
                      Category
                    </label>
                    <select id="category" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-1481">
                      {categories.map(category => <option key={category} value={category} data-id="element-1482">
                          {category}
                        </option>)}
                    </select>
                  </div>
                  <div data-id="element-1483">
                    <label htmlFor="certificationLevel" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1484">
                      Certification Level
                    </label>
                    <select id="certificationLevel" value={selectedCertificationLevel} onChange={e => setCertificationLevel(e.target.value === 'All' ? 'All' : parseInt(e.target.value))} className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md" data-id="element-1485">
                      {certificationLevels.map(level => <option key={level} value={level} data-id="element-1486">
                          {level === 'All' ? 'All Levels' : `Level ${level}`}
                        </option>)}
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
          {/* Product Review Cards */}
          <div className="grid grid-cols-1 gap-8" data-id="element-1487">
            {filteredProducts.length > 0 ? filteredProducts.map(product => <motion.div key={product.id} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-md" data-id="element-1488">
                  <div className="flex flex-col md:flex-row" data-id="element-1489">
                    <div className="md:w-1/3 h-64 md:h-auto" data-id="element-1490">
                      <img src={product.image} alt={product.name} className="w-full h-full object-cover" data-id="element-1491" />
                    </div>
                    <div className="p-6 md:w-2/3" data-id="element-1492">
                      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-4" data-id="element-1493">
                        <div data-id="element-1494">
                          <div className="mb-2" data-id="element-1495">
                            <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full uppercase font-semibold tracking-wide" data-id="element-1496">
                              {product.category}
                            </span>
                          </div>
                          <h3 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-1497">
                            {product.name}
                          </h3>
                          <p className="text-gray-600 text-sm mb-2" data-id="element-1498">
                            By {product.company}
                          </p>
                          {getCertificationBadge(product.certificationLevel)}
                        </div>
                        <div className="mt-4 sm:mt-0" data-id="element-1499">
                          {getRecommendationBadge(product.recommendation)}
                        </div>
                      </div>
                      <div className="mb-4" data-id="element-1500">
                        <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1501">
                          Health Claim
                        </h4>
                        <p className="text-gray-700 italic" data-id="element-1502">
                          "{product.healthClaim}"
                        </p>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4" data-id="element-1503">
                        <div data-id="element-1504">
                          <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2" data-id="element-1505">
                            Key Findings
                          </h4>
                          <ul className="space-y-1 text-sm text-gray-700" data-id="element-1506">
                            {product.keyFindings.map((finding, index) => <li key={index} className="flex items-start" data-id="element-1507">
                                <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1508" />
                                <span data-id="element-1509">{finding}</span>
                              </li>)}
                          </ul>
                        </div>
                        <div data-id="element-1510">
                          <div className="mb-4" data-id="element-1511">
                            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1512">
                              Efficacy Summary
                            </h4>
                            <p className="text-gray-700" data-id="element-1513">
                              {product.efficacySummary}
                            </p>
                          </div>
                          <div data-id="element-1514">
                            <h4 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-1" data-id="element-1515">
                              Transparency Score
                            </h4>
                            <div className="flex items-center" data-id="element-1516">
                              <div className="w-full bg-gray-200 rounded-full h-2.5 mr-2" data-id="element-1517">
                                <div className="bg-blue-600 h-2.5 rounded-full" style={{
                            width: `${product.transparencyScore}%`
                          }} data-id="element-1518"></div>
                              </div>
                              <span className="text-sm font-medium text-gray-700" data-id="element-1519">
                                {product.transparencyScore}%
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="mt-6" data-id="element-1520">
                        <Link to={`/product/${product.id}`} className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium" data-id="element-1521">
                          Read full review{' '}
                          <ArrowRightIcon size={16} className="ml-1" data-id="element-1522" />
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>) : <div className="text-center py-12 bg-gray-50 rounded-lg" data-id="element-1523">
                <SearchIcon size={48} className="mx-auto text-gray-400 mb-4" data-id="element-1524" />
                <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1525">
                  No products found
                </h3>
                <p className="text-gray-600" data-id="element-1526">
                  Try adjusting your filters to find more products
                </p>
              </div>}
          </div>
        </div>
      </section>

      {/* Why Product Reviews Matter */}
      <section className="py-16 bg-gray-50" data-id="element-1527">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1528">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-16" data-id="element-1529">
            <div className="flex items-center justify-center mb-8" data-id="element-1530">
              <div className="bg-blue-100 p-3 rounded-full" data-id="element-1531">
                <TargetIcon size={28} className="text-blue-600" data-id="element-1532" />
              </div>
              <h2 className="ml-4 text-3xl font-bold text-gray-900" data-id="element-1533">
                Why Product Reviews Matter
              </h2>
            </div>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-12" data-id="element-1534">
              Evidence-based product reviews create value for multiple
              stakeholders in the wellness ecosystem.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1535">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="bg-blue-50 p-8 rounded-lg shadow-md" data-id="element-1536">
                <div className="flex items-center mb-6" data-id="element-1537">
                  <UserIcon size={24} className="text-blue-600 mr-3" data-id="element-1538" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1539">
                    For Consumers
                  </h3>
                </div>
                <p className="text-gray-700 mb-4" data-id="element-1540">
                  They can finally cut through marketing hype and see which
                  products actually deliver measurable benefits.
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-1541">
                  <li className="flex items-start" data-id="element-1542">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1543" />
                    <span data-id="element-1544">Make informed decisions based on evidence</span>
                  </li>
                  <li className="flex items-start" data-id="element-1545">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1546" />
                    <span data-id="element-1547">
                      Understand what products actually do in the body
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1548">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1549" />
                    <span data-id="element-1550">Avoid wasting money on ineffective products</span>
                  </li>
                </ul>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="bg-blue-50 p-8 rounded-lg shadow-md" data-id="element-1551">
                <div className="flex items-center mb-6" data-id="element-1552">
                  <BuildingIcon size={24} className="text-blue-600 mr-3" data-id="element-1553" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1554">
                    For Companies
                  </h3>
                </div>
                <p className="text-gray-700 mb-4" data-id="element-1555">
                  A strong review backed by certification builds brand trust and
                  credibility in a competitive $120B+ industry.
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-1556">
                  <li className="flex items-start" data-id="element-1557">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1558" />
                    <span data-id="element-1559">
                      Differentiate products with scientific validation
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1560">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1561" />
                    <span data-id="element-1562">Build consumer trust through transparency</span>
                  </li>
                  <li className="flex items-start" data-id="element-1563">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1564" />
                    <span data-id="element-1565">Gain credibility with healthcare professionals</span>
                  </li>
                </ul>
              </motion.div>
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="bg-blue-50 p-8 rounded-lg shadow-md" data-id="element-1566">
                <div className="flex items-center mb-6" data-id="element-1567">
                  <BarChart4Icon size={24} className="text-blue-600 mr-3" data-id="element-1568" />
                  <h3 className="text-xl font-bold text-gray-900" data-id="element-1569">
                    For the Market
                  </h3>
                </div>
                <p className="text-gray-700 mb-4" data-id="element-1570">
                  Reviews create pressure for higher standards, shifting the
                  industry toward evidence-first wellness.
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-1571">
                  <li className="flex items-start" data-id="element-1572">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1573" />
                    <span data-id="element-1574">Elevate standards across the entire industry</span>
                  </li>
                  <li className="flex items-start" data-id="element-1575">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1576" />
                    <span data-id="element-1577">
                      Create transparency in a historically opaque market
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-1578">
                    <CheckCircleIcon size={16} className="text-blue-600 mr-2 flex-shrink-0 mt-1" data-id="element-1579" />
                    <span data-id="element-1580">Drive innovation based on real efficacy</span>
                  </li>
                </ul>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* How They're Shared */}
      <section className="py-16 bg-white" data-id="element-1581">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1582">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="mb-16" data-id="element-1583">
            <div className="flex items-center justify-center mb-8" data-id="element-1584">
              <div className="bg-blue-100 p-3 rounded-full" data-id="element-1585">
                <GlobeIcon size={28} className="text-blue-600" data-id="element-1586" />
              </div>
              <h2 className="ml-4 text-3xl font-bold text-gray-900" data-id="element-1587">
                How They're Shared
              </h2>
            </div>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-12" data-id="element-1588">
              Ukweli Product Reviews are distributed through multiple channels
              to maximize their impact.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1589">
              <div className="bg-white p-6 rounded-lg shadow-md text-center" data-id="element-1590">
                <div className="inline-block bg-blue-100 p-4 rounded-full mb-4" data-id="element-1591">
                  <GlobeIcon size={32} className="text-blue-600" data-id="element-1592" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1593">
                  Online Directory
                </h3>
                <p className="text-gray-700" data-id="element-1594">
                  Published in an open-access online directory maintained by
                  Ukweli, searchable by product category, health concern, or
                  certification level.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center" data-id="element-1595">
                <div className="inline-block bg-blue-100 p-4 rounded-full mb-4" data-id="element-1596">
                  <UserIcon size={32} className="text-blue-600" data-id="element-1597" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1598">
                  Professional Network
                </h3>
                <p className="text-gray-700" data-id="element-1599">
                  Shared with clinicians, researchers, and policymakers through
                  professional channels and healthcare networks.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-md text-center" data-id="element-1600">
                <div className="inline-block bg-blue-100 p-4 rounded-full mb-4" data-id="element-1601">
                  <BarChart4Icon size={32} className="text-blue-600" data-id="element-1602" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-1603">
                  Public Education
                </h3>
                <p className="text-gray-700" data-id="element-1604">
                  Simplified into infographics and video explainers for public
                  education, making the science accessible to everyone.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50" data-id="element-1605">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1606">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="bg-blue-50 rounded-xl p-8 md:p-12 text-center" data-id="element-1607">
            <h2 className="text-3xl font-bold text-gray-900 mb-6" data-id="element-1608">
              Submit a Product for Review
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8" data-id="element-1609">
              Are you a manufacturer of wellness products? Get your products
              certified and reviewed by our independent scientific team.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1610">
              <Link to="/certification" className="px-6 py-3 bg-blue-600 text-white font-medium rounded-md shadow-md hover:bg-blue-700 transition-colors duration-300 flex items-center justify-center" data-id="element-1611">
                Apply for Certification{' '}
                <ArrowRightIcon size={16} className="ml-2" data-id="element-1612" />
              </Link>
              <Link to="/research" className="px-6 py-3 bg-white text-blue-600 font-medium rounded-md shadow-md hover:bg-gray-100 transition-colors duration-300" data-id="element-1613">
                Learn About Our Research
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};